import { useState, useEffect, useCallback } from 'react';
import { familiarToUnit, unitToFamiliar, loadedValueToFamiliar } from '../utils/familiarPricing';
import { getSoftwareLicensingModel } from '../utils/partNumberUtils.jsx';

/**
 * Special Pricing inputs, extracted verbatim from StorageConfiguratorApp
 * (CONF-140 Phase 0b): the temp strings behind the controlled inputs, the
 * familiar<->unit conversion for term-based vendors, discount months, and the
 * Registered/Un-Registered toggle.
 *
 * @param {{ data: { pricing: object, viewState: object, config: object, vendor: object },
 *           config: { updateConfig: (patch: object) => void } }} deps
 */
export function useSpecialPricingInputs({ data, config: cfg }) {
  const { pricing, viewState, config, vendor } = data;
  const { updateConfig } = cfg;
  const regMultiplier = vendor.businessRules.registrationMultiplier;

  // Term-based vendors (e.g. SMC Software Only) price software per TB for the
  // whole term, but Special Pricing still accepts the familiar $/10TB/month
  // figure sales thinks in. swModel/swModelMonths drive the conversion;
  // familiarScale is 1 on classic vendors (AIC/DELL) so it is the identity there.
  const swModel = getSoftwareLicensingModel(config.serviceOption, vendor);
  const swModelMonths = pricing.subscriptionMonths ?? config.subscriptionMonths;

  // Unit-price bases used by the blur handlers to restore a valid display value.
  const [baseSsdSoftware, setBaseSsdSoftware] = useState(pricing.baseSsdSoftware);
  const [baseHddSoftware, setBaseHddSoftware] = useState(pricing.baseHddSoftware);

  // Controlled-input strings.
  const [tempSsdSoftware, setTempSsdSoftware] = useState(String(pricing.baseSsdSoftware));
  const [tempHddSoftware, setTempHddSoftware] = useState(String(pricing.baseHddSoftware));
  const [tempDiscountMonths, setTempDiscountMonths] = useState(String(pricing.discountMonths));

  // Keep the familiar-price inputs in sync when the underlying unit price or the
  // term changes elsewhere (duration/service/quarter changes, Rule 4 reprice,
  // config loads) so the input keeps showing e.g. 225/16 across duration
  // switches instead of the raw stored unit price.
  useEffect(() => {
    const ssdFamiliar = unitToFamiliar(pricing.baseSsdSoftware, swModel, swModelMonths);
    const hddFamiliar = unitToFamiliar(pricing.baseHddSoftware, swModel, swModelMonths);
    if (parseFloat(tempSsdSoftware) !== ssdFamiliar) setTempSsdSoftware(String(ssdFamiliar));
    if (parseFloat(tempHddSoftware) !== hddFamiliar) setTempHddSoftware(String(hddFamiliar));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricing.baseSsdSoftware, pricing.baseHddSoftware, swModel, swModelMonths]);

  const handleRegToggle = useCallback(() => {
    updateConfig({ isNoReg: !viewState.isNoReg });
  }, [updateConfig, viewState.isNoReg]);

  const handleSsdSoftwareChange = (e) => {
    const value = parseFloat(e.target.value);
    setTempSsdSoftware(e.target.value);
    if (!isNaN(value)) {
      // Typed value is the familiar figure; convert to the stored per-term unit
      // price first — regMultiplier applies AFTER this conversion (spec §5.5).
      const unitValue = familiarToUnit(value, swModel, swModelMonths);
      setBaseSsdSoftware(unitValue);
      const adjustedValue = viewState.isNoReg ? Number(unitValue.toFixed(2)) : Number((unitValue * regMultiplier).toFixed(2));
      const newSsdLicenseUnitPrice = adjustedValue - pricing.ssdSupportUnitPrice;
      updateConfig({ ssdSoftware: adjustedValue, baseSsdSoftware: unitValue, ssdLicenseUnitPrice: newSsdLicenseUnitPrice });
    }
  };

  const handleSsdSoftwareBlur = () => {
    if (isNaN(parseFloat(tempSsdSoftware))) {
      setTempSsdSoftware(String(unitToFamiliar(baseSsdSoftware, swModel, swModelMonths)));
    }
  };

  const handleHddSoftwareChange = (e) => {
    const value = parseFloat(e.target.value);
    setTempHddSoftware(e.target.value);
    if (!isNaN(value)) {
      const unitValue = familiarToUnit(value, swModel, swModelMonths);
      setBaseHddSoftware(unitValue);
      const adjustedValue = viewState.isNoReg ? Number(unitValue.toFixed(2)) : Number((unitValue * regMultiplier).toFixed(2));
      const newHddLicenseUnitPrice = adjustedValue - pricing.hddSupportUnitPrice;
      updateConfig({ hddSoftware: adjustedValue, baseHddSoftware: unitValue, hddLicenseUnitPrice: newHddLicenseUnitPrice });
    }
  };

  const handleHddSoftwareBlur = () => {
    if (isNaN(parseFloat(tempHddSoftware))) {
      setTempHddSoftware(String(unitToFamiliar(baseHddSoftware, swModel, swModelMonths)));
    }
  };

  const handleDiscountMonthsChange = (e) => {
    const value = parseFloat(e.target.value);
    setTempDiscountMonths(e.target.value);
    if (!isNaN(value)) updateConfig({ discountMonths: value });
  };

  const handleDiscountMonthsBlur = () => {
    if (isNaN(parseFloat(tempDiscountMonths))) setTempDiscountMonths(pricing.discountMonths.toString());
  };

  /**
   * After a saved quote loads (App.js:883–893): reflect its software prices in
   * the inputs. Licensing model is resolved from the LOADED config + vendor, not
   * render-time values, because a cross-vendor load's switchVendor only takes
   * effect next render.
   */
  const applyLoadedSoftwarePrices = useCallback((savedConfig, loadedVendor) => {
    const loadedSwModel = getSoftwareLicensingModel(savedConfig.serviceOption, loadedVendor);
    const loadedMonths = savedConfig.subscriptionMonths ?? swModelMonths;
    if (savedConfig.ssdSoftware !== undefined) {
      setTempSsdSoftware(String(loadedValueToFamiliar(savedConfig.ssdSoftware, loadedSwModel, loadedMonths)));
    }
    if (savedConfig.hddSoftware !== undefined) {
      setTempHddSoftware(String(loadedValueToFamiliar(savedConfig.hddSoftware, loadedSwModel, loadedMonths)));
    }
  }, [swModelMonths]);

  return {
    tempSsdSoftware, tempHddSoftware, tempDiscountMonths,
    handleSsdSoftwareChange, handleSsdSoftwareBlur,
    handleHddSoftwareChange, handleHddSoftwareBlur,
    handleDiscountMonthsChange, handleDiscountMonthsBlur,
    handleRegToggle,
    swModel, swModelMonths,
    applyLoadedSoftwarePrices,
  };
}

export default useSpecialPricingInputs;
